import { useEffect, useState } from 'react'
import { Check, Zap, ShieldCheck, BarChart2, Link, Users, Clock, AlertCircle, Mail } from 'lucide-react'
import api from '../../services/api'

const ADDON_ICONS: Record<string, React.ElementType> = {
  lone_worker: ShieldCheck,
  iso_compliance: Check,
  erp_integration: Link,
  advanced_analytics: BarChart2,
}

const PLAN_BADGE: Record<string, { bg: string; color: string }> = {
  trial: { bg: '#FEF3E2', color: '#8A4E00' },
  standard: { bg: '#E6F0FB', color: '#0A3D6E' },
  professional: { bg: '#EAE6FF', color: '#4A3A8C' },
  enterprise: { bg: '#EBF0F8', color: '#1B2B4B' },
}

export default function BillingSettings() {
  const [billing, setBilling] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [requested, setRequested] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = async () => {
    try {
      const { data } = await api.get('/billing')
      setBilling(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load billing info')
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const requestUpgrade = async (plan?: string, addonKey?: string) => {
    const key = plan ? `plan_${plan}` : addonKey!
    setSaving(key)
    setError('')
    try {
      await api.post('/billing/request-upgrade', { plan, addon_key: addonKey })
      setRequested(plan
        ? billing?.all_plans?.find((p: any) => p.key === plan)?.label ?? plan
        : billing?.available_addons?.find((a: any) => a.key === addonKey)?.label ?? addonKey
      )
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send request. Please email support@hseqintelligence.com.au')
    }
    setSaving(null)
  }

  if (loading) return (
    <div className="flex items-center justify-center h-48" style={{ color: 'var(--text-muted)' }}>Loading…</div>
  )

  if (!billing) return (
    <div className="max-w-4xl mx-auto p-6">
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-xl text-sm" style={{ background: '#FFEBE6', color: '#D93025' }}>
          <AlertCircle size={16} />{error}
        </div>
      )}
    </div>
  )

  const badge = PLAN_BADGE[billing.plan] || PLAN_BADGE.standard
  const usageUserPct = billing.usage.max_users > 0 ? Math.round((billing.usage.users / billing.usage.max_users) * 100) : 0
  const usageSitePct = billing.usage.max_sites > 0 ? Math.round((billing.usage.sites / billing.usage.max_sites) * 100) : 0

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>Billing & Subscription</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Manage your plan and add-ons</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl text-sm" style={{ background: '#FFEBE6', color: '#D93025' }}>
          <AlertCircle size={16} />{error}
        </div>
      )}

      {requested && (
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: '#E8F8EA', border: '1px solid #3DB54A' }}>
          <Check size={18} color="#2A7A35" className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-sm" style={{ color: '#2A7A35' }}>Request received — {requested}</p>
            <p className="text-xs mt-0.5" style={{ color: '#2A7A35' }}>
              Our team will contact you within 1 business day to arrange invoicing and activate your plan.
            </p>
          </div>
        </div>
      )}

      {/* Current plan */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Current Plan</p>
            <div className="flex items-center gap-2">
              <h2 className="font-extrabold text-xl" style={{ color: 'var(--text)' }}>{billing.plan_label}</h2>
              <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: badge.bg, color: badge.color }}>
                {billing.plan.toUpperCase()}
              </span>
            </div>
            {billing.monthly_price > 0 && (
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
                ${billing.monthly_price}/month AUD ex GST
              </p>
            )}
          </div>
          {billing.is_trial && billing.trial_days_left !== null && (
            <div className="text-right">
              <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: billing.trial_days_left <= 5 ? '#D93025' : '#F5921E' }}>
                <Clock size={14} />
                {billing.trial_days_left > 0 ? `${billing.trial_days_left} days left` : 'Expired'}
              </div>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Free trial</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
              <span className="flex items-center gap-1"><Users size={12} /> Users</span>
              <span>{billing.usage.users} / {billing.usage.max_users < 0 ? '∞' : billing.usage.max_users}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(100, usageUserPct)}%`,
                background: usageUserPct >= 90 ? '#D93025' : usageUserPct >= 70 ? '#F5921E' : '#3DB54A',
              }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
              <span>Sites</span>
              <span>{billing.usage.sites} / {billing.usage.max_sites < 0 ? '∞' : billing.usage.max_sites}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(100, usageSitePct)}%`,
                background: usageSitePct >= 90 ? '#D93025' : usageSitePct >= 70 ? '#F5921E' : '#1B2B4B',
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-bold text-base mb-1" style={{ color: 'var(--text)' }}>Plans</h2>
        <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
          Invoiced monthly — no credit card required. Request an upgrade and we'll be in touch within 1 business day.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {billing.all_plans.map((plan: any) => (
            <div
              key={plan.key}
              className="rounded-2xl p-4 flex flex-col"
              style={{
                border: plan.current ? '2px solid #1B2B4B' : '1px solid var(--border)',
                background: plan.current ? '#EBF0F8' : 'white',
              }}
            >
              {plan.current && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full mb-2 self-start" style={{ background: '#1B2B4B', color: 'white' }}>
                  Current
                </span>
              )}
              <h3 className="font-bold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{plan.label}</h3>
              <p className="font-extrabold text-xl mb-1" style={{ color: '#1B2B4B' }}>
                {plan.monthly_price === 0 ? 'Free' : `$${plan.monthly_price}`}
                {plan.monthly_price > 0 && <span className="text-xs font-medium text-gray-400">/mo</span>}
              </p>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                {plan.max_users < 0 ? 'Unlimited users' : `Up to ${plan.max_users} users`}
              </p>
              {!plan.current && plan.key !== 'trial' && (
                <button
                  onClick={() => requestUpgrade(plan.key)}
                  disabled={!!saving || !!requested}
                  className="mt-auto py-2 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-1.5"
                  style={{ background: (saving || requested) ? 'var(--text-muted)' : '#1B2B4B' }}
                >
                  {saving === `plan_${plan.key}` ? 'Sending…' : requested ? 'Requested ✓' : (
                    <><Mail size={12} /> Request Upgrade</>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <h2 className="font-bold text-base mb-1" style={{ color: 'var(--text)' }}>Add-ons</h2>
        <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Extend your plan with premium modules — invoiced with your monthly subscription.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {billing.available_addons.map((addon: any) => {
            const Icon = ADDON_ICONS[addon.key] || Zap
            return (
              <div
                key={addon.key}
                className="rounded-2xl p-4 flex items-start gap-4"
                style={{
                  border: addon.active ? '1.5px solid #3DB54A' : '1px solid var(--border)',
                  background: addon.active ? '#E8F8EA' : 'white',
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: addon.active ? '#3DB54A' : '#EBF0F8' }}>
                  <Icon size={18} color={addon.active ? 'white' : '#1B2B4B'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-bold text-sm" style={{ color: 'var(--text)' }}>{addon.label}</h3>
                    {addon.active && (
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#3DB54A', color: 'white' }}>Active</span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5 mb-2" style={{ color: 'var(--text-muted)' }}>{addon.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold" style={{ color: '#1B2B4B' }}>${addon.monthly_price}/mo</span>
                    {!addon.active && (
                      <button
                        onClick={() => requestUpgrade(undefined, addon.key)}
                        disabled={!!saving || !!requested}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"
                        style={{
                          background: '#EBF0F8',
                          color: '#1B2B4B',
                          opacity: (saving || requested) ? 0.6 : 1,
                          cursor: (saving || requested) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {saving === addon.key ? '…' : <><Mail size={11} /> Request</>}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="text-center py-4">
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Questions about pricing or invoicing?{' '}
          <a href="mailto:support@hseqintelligence.com.au" className="underline">support@hseqintelligence.com.au</a>
        </p>
      </div>
    </div>
  )
}
